import {
  DatePicker,
  DropdownPanel,
  PickerInputField,
} from "@smartsuite/react-ui/lib";
import type { PillPickerEditorCommands } from "@smartsuite/smartdoc";
import type { PillNodeAttrs } from "@smartsuite/smartdoc/lib/esm/extensions/pill/extension";
import i18n from "i18next";
import moment from "moment-timezone";
import type { FC } from "react";
import { useEffect, useRef, useState } from "react";
import {
  StyledTitle,
  StyledTopLineOuter,
} from "../../../../../../../../../../components/CaptionBlock/styles";
import { useTheme } from "../../../../../../../../../../stores";
import { useRootStore } from "../../../../../../../../../../stores/hooks";
import { getLocalCssColorVariables } from "../../../../../../../../../../utils/cssVariablesUtils";
import { getLocaleByLangCode } from "../../../../../../../../../../utils/locale";
import { POSITION_AT, POSITION_MY } from "../../../../../../style";
import { StyledContainerWrapperNoPills } from "../../../../../style";
import { useRepeatedSchedule } from "../../repeated-schedule.hook";
import * as S from "./style";

interface Props {
  title?: string;
}

export const StartingFrom: FC<Props> = ({ title }) => {
  const theme = useTheme();
  const [isOpen, setOpen] = useState<boolean>(false);
  const [isInitial, setIsInitial] = useState<boolean>(true);
  const commandsRef = useRef<PillPickerEditorCommands>();
  const buttonRef = useRef();

  const { scheduleStore } = useRootStore();
  const { getStartFromDate, setStartFromDate } = useRepeatedSchedule();

  const [selectedDate, setSelectedDate] = useState<number | undefined>();

  useEffect(() => {
    if (getStartFromDate() > 0) {
      setSelectedDate(getStartFromDate());
    }
  }, [scheduleStore.schedule]);

  const [pills, setPills] = useState<PillNodeAttrs[]>([]);

  const addPill = (attrs: PillNodeAttrs): void => {
    setOpen(false);
    setPills([attrs]);
  };

  useEffect(() => {
    if (selectedDate !== undefined && isInitial) {
      setIsInitial(false);
      setInitialDate();
    }
  }, [selectedDate]);

  if (selectedDate === undefined) {
    return null;
  }

  const formatDate = (): string => {
    return moment(selectedDate * 1000).format("MMM DD, YYYY");
  };

  const setInitialDate = (): void => {
    addPill({
      icon: "",
      // @ts-expect-error
      isRemovable: false,
      title: formatDate(),
      value: JSON.stringify({
        value: selectedDate,
        target: "date",
      }),
    });
  };

  const buildMenu = (): React.ReactNode => {
    return (
      <DropdownPanel
        targetElementRef={buttonRef}
        isActive={isOpen}
        at={POSITION_AT}
        my={POSITION_MY}
        minWidth={400}
        arrowBackground="white"
        portalStyles={getLocalCssColorVariables(theme)}
        clickOutsideExcept={[".is-exclude-click-outside"]}
        onClickOutside={() => {
          setOpen(false);
        }}
      >
        <DatePicker
          showTimePicker={false}
          value={new Date(selectedDate * 1000)}
          onChange={(date: Date) => {
            setSelectedDate(date.getTime() / 1000);
          }}
          onSelectionDone={() => {
            addPill({
              icon: "",
              title: formatDate(),
              value: JSON.stringify({
                value: selectedDate,
                target: "date",
              }),
              // @ts-expect-error
              isRemovable: false,
            });
            setStartFromDate(selectedDate);
          }}
          onSelectionCanceled={() => {
            setOpen(false);
          }}
          zIndex={1000}
          locale={getLocaleByLangCode(i18n.language)}
        />
      </DropdownPanel>
    );
  };

  return (
    <S.DateWrapper>
      <StyledContainerWrapperNoPills>
        <StyledTopLineOuter>
          <StyledTitle>{title}</StyledTitle>
        </StyledTopLineOuter>

        <PickerInputField
          state={isOpen ? "active" : "default"}
          pickerIcon="date-select"
          // @ts-expect-error
          commandsRef={commandsRef}
          buttonRef={buttonRef}
          isOpen={isOpen}
          pills={pills}
          pillStyle={{
            textSize: "m",
          }}
          onClick={() => {
            if (!isOpen) {
              setOpen(true);
            }
          }}
          autofocus={false}
        >
          {buildMenu()}
        </PickerInputField>
      </StyledContainerWrapperNoPills>
    </S.DateWrapper>
  );
};
